import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Tab, Tabs } from '@mui/material';
import SpotPlacementSettings from './SpotPlacementSettings/SpotPlacementSettings';
import MemberAppSettings from './MemberAppSettings/MemberAppSettings';
import KioskSetting from './KioskSettings/KioskSetting';
import { getAreaListByBranchId, getGolfSpotsByAreaId, updateGolfAreaSpotById } from '../../api/golf';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../utils/methods';

const StoreTab7 = ({ branchId, centerId }) => {
  const [loading, setLoading] = useState(true);
  const { control, setValue } = useForm({
    defaultValues: {
      selectedArea: '',
    },
  });
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const [selectedTabChild, setSelectedTabChild] = useState(1);
  const [openSpotCreateModal, setOpenSpotCreateModal] = useState(false);
  const [spotAreas, setSpotAreas] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [openSpotSettingModal, setOpenSpotSettingModal] = useState(false);
  const [selectedAreaForEdit, setSelectedAreaForEdit] = useState(null);

  // 스팟 위젯 조회
  const buildAreaWithBoxes = useCallback(
    async area => {
      try {
        const spotData = await getGolfSpotsByAreaId(user.token, area.golfAreaId);
        let spotsArray = [];
        if (Array.isArray(spotData)) {
          spotsArray = spotData;
        } else if (Array.isArray(spotData?.spots)) {
          spotsArray = spotData.spots;
        } else if (spotData && typeof spotData === 'object' && spotData.id) {
          spotsArray = [spotData];
        }

        const boxes = spotsArray.map((spot, index) => ({
          ...spot,
          id: spot.id,
          row: spot.row,
          col: spot.column,
        }));

        return {
          ...area,
          boxes,
        };
      } catch (error) {
        console.error(`[buildAreaWithBoxes] Area ${area.golfAreaId} spot 데이터 조회 실패:`, error);
        return {
          ...area,
          boxes: [],
        };
      }
    },
    [user.token],
  );

  // 스팟 구역 전체 조회
  useEffect(() => {
    const fetchAreaList = async () => {
      try {
        if (user?.token && branchId) {
          const response = await getAreaListByBranchId(user.token, branchId);
          if (response && Array.isArray(response)) {
            const areasWithBoxes = await Promise.all(response.map(area => buildAreaWithBoxes(area)));
            setSpotAreas(areasWithBoxes);
          }
        }
      } catch (error) {
        console.log('골프 관리 스팟 목록 조회 실패', error);
      }
    };
    fetchAreaList();
  }, [branchId, centerId, user?.token, buildAreaWithBoxes]);

  // spotAreas가 로드되면 첫 번째 area 자동 선택
  useEffect(() => {
    if (spotAreas.length > 0 && selectedAreaId === null) {
      const firstAreaId = spotAreas[0].golfAreaId;
      setSelectedAreaId(firstAreaId);
      setValue('selectedArea', firstAreaId.toString(), { shouldValidate: true });
    }
  }, [spotAreas, selectedAreaId, setValue]);

  // 스팟 구역 새로고침 (생성/수정 후 호출)
  const refreshSpotAreasWithSelection = async () => {
    try {
      if (user?.token && branchId) {
        const response = await getAreaListByBranchId(user.token, branchId);
        if (response && Array.isArray(response)) {
          const areasWithBoxes = await Promise.all(response.map(area => buildAreaWithBoxes(area)));
          setSpotAreas(areasWithBoxes);
          if (!selectedAreaForEdit && areasWithBoxes.length > 0) {
            const newArea = areasWithBoxes[areasWithBoxes.length - 1];
            setSelectedAreaId(newArea.golfAreaId);
            setValue('selectedArea', newArea.golfAreaId.toString());
          }
        }
        setOpenSpotCreateModal(false);
        setSelectedAreaForEdit(null);
      }
    } catch (error) {
      console.error('스팟 데이터 새로고침 실패:', error);
    }
  };

  // 스팟 구역 새로고침 (일반용)
  const refreshSpotAreas = async () => {
    try {
      if (user?.token && branchId) {
        const response = await getAreaListByBranchId(user.token, branchId);
        if (response && Array.isArray(response)) {
          const areasWithBoxes = await Promise.all(response.map(area => buildAreaWithBoxes(area)));
          setSpotAreas(areasWithBoxes);
          return areasWithBoxes; // 업데이트된 배열 반환
        }
      }
      return []; // 데이터가 없으면 빈 배열 반환
    } catch (error) {
      console.error('스팟 데이터 새로고침 실패:', error);
      return []; // 에러 발생 시 빈 배열 반환
    }
  };

  const handleChange = (e, newValue) => {
    setSelectedTabChild(newValue);

    setLoading(!loading);
  };

  const handleSpotCreateModal = () => {
    setOpenSpotCreateModal(true);
  };

  const handleSpotCreateModalClose = () => {
    setOpenSpotCreateModal(false);
    setSelectedAreaForEdit(null);
  };

  const handleSpotEditModalOpen = area => {
    setSelectedAreaForEdit(area);
    setOpenSpotCreateModal(true);
  };

  const handleSpotSettingModal = () => {
    setOpenSpotSettingModal(true);
  };

  const handleSpotSettingModalClose = () => {
    setOpenSpotSettingModal(false);
  };

  // 위젯 체크 - spotStatus 업데이트
  const handleCheckSpotSetting = async (boxId, newStatus) => {
    try {
      await updateGolfAreaSpotById(user?.token, boxId, { spotStatus: newStatus });

      setSpotAreas(prevAreas => {
        const updatedAreas = prevAreas.map(area => {
          if (area.golfAreaId === selectedAreaId) {
            return {
              ...area,
              boxes: area.boxes
                ? area.boxes.map(box => (box.id === boxId ? { ...box, spotStatus: newStatus } : box))
                : [],
            };
          }
          return area;
        });
        return updatedAreas;
      });
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  return (
    <div>
      <Box sx={{ minHeight: '700px', mt: '20px' }}>
        <Tabs
          sx={{
            '& .MuiTab-root.MuiTab-textColorSecondary': {
              borderRadius: '12px',
              border: '1px solid #0F7F85',
              mr: 1.25,
              minWidth: '100px',
              minHeight: '40px !important',
              padding: '12px 40px',
              fontWeight: 500,
              fontSize: '13px',
            },
            '& .MuiTab-root.MuiTab-textColorSecondary.Mui-selected': {
              background: '#0F7F85',
              borderRadius: '12px',
              border: 'none',
              color: '#fff',
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTab-textColorSecondary.css-sopws0-MuiButtonBase-root-MuiTab-root': {
              borderRadius: '8px 8px 0px 0px',
              background: '#AEAEAE',
              border: 'none',
              color: '#fff',
            },
            '& .MuiTab-textColorSecondary.css-1gc9b8s-MuiButtonBase-root-MuiTab-root': {
              borderRadius: '8px 8px 0px 0px',
              background: '#AEAEAE',
              border: 'none',
              color: '#fff',
            },
            '& .MuiTab-textColorSecondary.css-sopws0-MuiButtonBase-root-MuiTab-root.Mui-selected': {
              borderRadius: '8px 8px 0px 0px',
            },
            '& .MuiTab-textColorSecondary.css-1gc9b8s-MuiButtonBase-root-MuiTab-root.Mui-selected': {
              borderRadius: '8px 8px 0px 0px',
            },
          }}
          value={selectedTabChild}
          onChange={handleChange}
          textColor="secondary"
          indicatorColor="secondary"
          aria-label="secondary tabs example">
          <Tab sx={{ mb: '0px', pb: '0px' }} value={1} label={t('Spot placement settings')} />
          <Tab value={2} label={t('Member app reservation settings')} />
          <Tab value={3} label={t('Kiosk settings')} />
        </Tabs>
        {selectedTabChild === 1 && (
          <SpotPlacementSettings
            spotAreas={spotAreas}
            selectedAreaId={selectedAreaId}
            setSelectedAreaId={setSelectedAreaId}
            control={control}
            setValue={setValue}
            handleCheckSpotSetting={handleCheckSpotSetting}
            handleSpotCreateModal={handleSpotCreateModal}
            handleSpotCreateModalClose={handleSpotCreateModalClose}
            handleSpotSettingModal={handleSpotSettingModal}
            handleSpotSettingModalClose={handleSpotSettingModalClose}
            openSpotCreateModal={openSpotCreateModal}
            openSpotSettingModal={openSpotSettingModal}
            handleSpotEditModalOpen={handleSpotEditModalOpen}
            selectedAreaForEdit={selectedAreaForEdit}
            branchId={branchId}
            refreshSpotAreas={refreshSpotAreasWithSelection}
            refreshSpotAreasForModal={refreshSpotAreas}
          />
        )}
        {selectedTabChild === 2 && <MemberAppSettings branchId={branchId} />}
        {selectedTabChild === 3 && <KioskSetting branchId={branchId} />}
      </Box>
    </div>
  );
};

export default StoreTab7;
